import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../Common/LoadingSpinner';
import { GoogleLogin } from '@react-oauth/google';
import { launchWhatsAppSignupPopup } from '../../utils/whatsappFlow';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { login, googleLogin, getRememberedEmail, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get the redirect path or default to dashboard
  const redirectPath = location.state?.from?.pathname || '/dashboard';

  const handleConnectionWithCode = async (code, wabaId = null, phoneId = null) => {
    try {
      const { requestHelpers } = require('../../services/api');
      await requestHelpers.put('/user/connect-whatsapp', {
        code,
        businessAccountId: wabaId,
        phoneNumberId: phoneId,
        redirectUri: window.location.origin + '/whatsapp-callback'
      });
      await refreshProfile();
    } catch (err) {
      console.error('Failed to auto-connect WhatsApp during login:', err);
    }
  };

  useEffect(() => {
    const handleMessage = (event) => {
      const isFacebook = event.origin === 'https://www.facebook.com' || event.origin === 'https://web.facebook.com';
      const isSelf = event.origin === window.location.origin;

      if (!isFacebook && !isSelf) return;

      // Handle Meta's Embedded Signup Message (could be string or object)
      let data = event.data;
      if (typeof data === 'string' && isFacebook) {
        try { data = JSON.parse(data); } catch (e) { }
      }

      // [CAPTURE] Meta's Direct Signal
      if (isFacebook && data.event === 'FINISH') {
        const metaData = data.data || {};
        const waba_id = metaData.waba_id || metaData.wabaId || metaData.businessAccountId;
        const phone_number_id = metaData.phone_number_id || metaData.phoneNumberId || metaData.phoneId;

        if (waba_id || phone_number_id) {
          console.log('✅ [LOGIN-WA] Captured IDs from Meta Signal:', { waba_id, phone_number_id });
          window._lastMetaIds = { waba_id, phone_number_id };
        }
      }


      if (data.type === 'WA_CONNECTION_SUCCESS') {
        const metaIds = window._lastMetaIds || {};
        handleConnectionWithCode(data.code, data.businessAccountId || metaIds.waba_id, data.phoneNumberId || metaIds.phone_number_id);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    // Pre-fill email if remembered
    const rememberedEmail = getRememberedEmail();
    if (rememberedEmail) {
      setFormData(prev => ({
        ...prev,
        email: rememberedEmail,
        rememberMe: true
      }));
    }
  }, [getRememberedEmail]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await login(formData.email, formData.password, formData.rememberMe);
      toast.success('Login successful!');

      // Check if WhatsApp is connected
      const user = response.user;
      const targetPath = (!user.whatsappConnected) ? '/connect-whatsapp' : redirectPath;

      navigate(targetPath, { replace: true });
    } catch (error) {
      // If user is unverified, handle redirection to OTP page
      if (error.response?.status === 403 && error.response?.data?.userId) {
        toast.info(error.response.data.message);
        navigate('/verify-otp', {
          state: {
            userId: error.response.data.userId,
            email: error.response.data.email
          }
        });
      } else {
        toast.error(error?.message || 'Login failed');
      }
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    try {
      const response = await googleLogin(credentialResponse.credential);
      toast.success('Login successful with Google!');

      // Check if WhatsApp is connected
      const user = response.user;
      let targetPath = redirectPath;

      if (!user.whatsappConnected) {
        const appId = process.env.REACT_APP_FACEBOOK_APP_ID || '1298260612181970';
        const configId = process.env.REACT_APP_FACEBOOK_CONFIG_ID || '3302994339877898';

        toast.info('Opening WhatsApp Onboarding...');
        launchWhatsAppSignupPopup(appId, configId);
        targetPath = '/dashboard'; // Go to dashboard
      }

      navigate(targetPath, { replace: true });
    } catch (error) {
      toast.error(error?.message || 'Google login failed');
      console.error('Google login error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    toast.error('Google login failed');
  };

  return (
    <div className="auth-page">
      <div className="floating-shapes">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="floating-shape"></div>
        ))}
      </div>

      <div className="wave-container">
        <div className="wave"></div>
        <div className="wave"></div>
        <div className="wave"></div>
      </div>

      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <div className="logo">
              <img src="/logo.svg" alt="MessagePe" className="auth-logo-img" />
            </div>
            <h2>Welcome Back</h2>
            <p>Sign in to your account to continue</p>
          </div>

          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="email" className="form-label">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                className="form-control"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  className="form-control"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                  tabIndex="-1"
                >
                  <i className={showPassword ? "fas fa-eye-slash" : "fas fa-eye"}></i>
                </button>
              </div>
            </div>

            <div className="form-options">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleChange}
                  disabled={loading}
                />
                <span className="checkmark"></span>
                Remember me
              </label>
              <Link to="/forgot-password" self className="forgot-password">
                Forgot password?
              </Link>
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <LoadingSpinner size="small" /> : 'Sign In'}
            </button>
          </form>

          <div className="auth-divider">
            <span>OR</span>
          </div>

          <div className="social-auth">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              useOneTap
              theme="outline"
              size="large"
              width="350"
              text="signin_with"
              shape="rectangular"
            />
          </div>

          <div className="auth-footer">
            <p>Don't have an account? <Link to="/signup">Sign up</Link></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;